import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { messagesNow } from '@/i18n'
import { messages } from './messages'
import { useTranslateArticleMutation, useLazyGetAiJobQuery } from '@/features/ai/aiApi'
import { instructionsApi } from '@/features/instructions/instructionsApi'
import { useAppDispatch } from '@/store/hooks'
import { translationFailedText } from '@/features/instructions/lib/translationFailureText'
import { REVIEW_IN_PROGRESS } from '@/features/moderation/lib/reviewInProgress'
import { reportStartFailure } from './translationStartFailure'
import { articleModerationApi } from '@/features/moderation/articleModerationApi'
import { translationInDraft } from '@/features/instructions/lib/translationInDraft'
import type { ModerationState } from '@/features/moderation/types'
import type { AiJobStatus } from '@/features/ai/types'
import type { Instruction } from '@/features/instructions/types'

/** Принудительное перечитывание `/moderation`; сбой — `undefined` (перевод всё равно готов). */
const refetchModeration = (
  dispatch: ReturnType<typeof useAppDispatch>,
  id: number,
): Promise<ModerationState | undefined> =>
  dispatch(
    articleModerationApi.endpoints.getArticleModeration.initiate(id, {
      forceRefetch: true,
      subscribe: false,
    }),
  )
    .unwrap()
    .catch(() => undefined)

const POLL_INTERVAL = 2000
const POLL_TIMEOUT = 120_000

export type TranslationStatus = AiJobStatus | 'idle' | 'already_fresh'

export const useTranslation = (instruction: Instruction | undefined) => {
  const dispatch = useAppDispatch()
  const [translateArticle] = useTranslateArticleMutation()
  const [fetchJob] = useLazyGetAiJobQuery()

  const [isTranslating, setIsTranslating] = useState(false)
  const [status, setStatus] = useState<TranslationStatus>('idle')
  const timerRef = useRef<ReturnType<typeof setInterval>>()
  const deadlineRef = useRef(0)

  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = undefined
  }, [])

  useEffect(() => cleanup, [cleanup])

  const pollJob = useCallback(
    (jobId: number) => {
      deadlineRef.current = Date.now() + POLL_TIMEOUT
      timerRef.current = setInterval(async () => {
        if (Date.now() > deadlineRef.current) {
          cleanup()
          setIsTranslating(false)
          setStatus('idle')
          toast.error(messagesNow(messages).translateTimeout)
          return
        }
        try {
          const job = await fetchJob(jobId, false).unwrap()
          setStatus(job.status)

          if (job.status === 'success') {
            cleanup()
            setIsTranslating(false)
            setStatus('idle')
            if (instruction) {
              dispatch(
                instructionsApi.util.invalidateTags([
                  { type: 'Instruction', id: instruction.id },
                  { type: 'Instruction', id: instruction.slug },
                  // Перевод живой статьи пишет в черновик — без него форма его не увидит.
                  { type: 'ArticleModeration', id: instruction.id },
                ]),
              )
            }
            // Решаем по перечитанной модерации: перевод, совпавший с живым, в черновик не
            // попал, и «Обновить» ему не нужно.
            const m = messagesNow(messages)
            const state = instruction
              ? await refetchModeration(dispatch, instruction.id)
              : undefined
            toast.success(translationInDraft(state) ? m.translateDoneDraft : m.translateDone)
          }

          if (job.status === 'failed') {
            cleanup()
            setIsTranslating(false)
            setStatus('idle')
            // Провал из-за ушедшей на проверку статьи: форма должна замёрзнуть сразу,
            // а не на следующем опросе (а у непроверявшейся статьи опроса нет вовсе).
            if (job.error_code === REVIEW_IN_PROGRESS && instruction)
              dispatch(
                instructionsApi.util.invalidateTags([
                  { type: 'ArticleModeration', id: instruction.id },
                ]),
              )
            toast.error(translationFailedText(job.error_code, job.error_message))
          }
        } catch {
          cleanup()
          setIsTranslating(false)
          setStatus('idle')
          toast.error(messagesNow(messages).translateStatusFailed)
        }
      }, POLL_INTERVAL)
    },
    [fetchJob, cleanup, dispatch, instruction],
  )

  const translate = useCallback(
    async (force = false) => {
      if (!instruction) return
      setIsTranslating(true)
      setStatus('pending')
      try {
        const result = await translateArticle({
          articleId: instruction.id,
          body: { target_lang: 'en', force },
        }).unwrap()
        pollJob(result.job_id)
      } catch (err: unknown) {
        setIsTranslating(false)
        setStatus(reportStartFailure(err, () => translate(true)))
      }
    },
    [instruction, translateArticle, pollJob],
  )

  return { translate, isTranslating, status }
}
